import React, { useRef, useEffect } from 'react';
import { Box, Typography, Card, CardContent, useTheme } from '@mui/material';

interface BreakingEnergyChartProps {
  E_DCH_max: number;  // Joules
  E_CH_max: number;   // Joules
  P_0: number;        // Peak power (W)
  t_br: number;       // Breaking time (s)
  E_max: number;      // Maximum energy for Module 2 (J)
}

const BreakingEnergyChart: React.FC<BreakingEnergyChartProps> = ({
  E_DCH_max,
  E_CH_max,
  P_0,
  t_br,
  E_max
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const theme = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // High-DPI canvas scaling for crisp text
    const dpr = window.devicePixelRatio || 1;
    const displayWidth = 1000;
    const displayHeight = 650;

    // Set actual canvas size (accounting for device pixel ratio)
    canvas.width = displayWidth * dpr;
    canvas.height = displayHeight * dpr;

    // Scale context to match device pixel ratio
    ctx.scale(dpr, dpr);

    // Clear canvas
    ctx.clearRect(0, 0, displayWidth, displayHeight);
    ctx.fillStyle = theme.palette.mode === 'dark' ? '#1a1a2e' : '#ffffff';
    ctx.fillRect(0, 0, displayWidth, displayHeight);

    const padding = 60;
    const sectionWidth = (displayWidth - padding * 2) / 2;
    const topHeight = 180;
    const chartSpacing = 30; // Space between charts

    // Helper function for power profile miniatures
    const drawPowerProfile = (x: number, y: number, width: number, height: number, highlightType: 'dch' | 'ch' | 'breaking') => {
      // Grid
      ctx.strokeStyle = theme.palette.mode === 'dark' ? '#333' : '#e0e0e0';
      ctx.lineWidth = 0.5;
      for (let i = 0; i <= 5; i++) {
        const gridY = y + (i / 5) * height;
        ctx.beginPath();
        ctx.moveTo(x, gridY);
        ctx.lineTo(x + width, gridY);
        ctx.stroke();
      }
      for (let i = 0; i <= 5; i++) {
        const gridX = x + (i / 5) * width;
        ctx.beginPath();
        ctx.moveTo(gridX, y);
        ctx.lineTo(gridX, y + height);
        ctx.stroke();
      }

      // Simplified power profile (based on reference image)
      ctx.strokeStyle = '#1f77b4';
      ctx.lineWidth = 1.5;
      ctx.beginPath();

      const points = [
        [0, 0.9], [0.05, 0.9], [0.1, 0.3], [0.15, 0.5], [0.2, 0.1],
        [0.25, 0.9], [0.3, 0.4], [0.35, 0.6], [0.4, 0.2], [0.45, 0.1],
        [0.5, 0.8], [0.55, 0.5], [0.6, 0.7], [0.65, 0.4], [0.7, 0.6],
        [0.75, 0.5], [0.8, 0.7], [0.85, 0.3], [0.9, 0.5], [0.95, 0.4], [1, 0.3]
      ];

      points.forEach(([px, py], i) => {
        const xPos = x + px * width;
        const yPos = y + height - py * height;
        if (i === 0) ctx.moveTo(xPos, yPos);
        else ctx.lineTo(xPos, yPos);
      });
      ctx.stroke();

      // Highlight specific energy region based on type
      if (highlightType === 'dch' || highlightType === 'ch') {
        // Arrow pointing to peak
        const arrowX = highlightType === 'dch' ? x + width * 0.25 : x + width * 0.6;
        const arrowY = y - 10;

        ctx.fillStyle = theme.palette.mode === 'dark' ? '#fff' : '#000';
        ctx.strokeStyle = theme.palette.mode === 'dark' ? '#fff' : '#000';
        ctx.lineWidth = 2;

        // Arrow line
        ctx.beginPath();
        ctx.moveTo(arrowX, arrowY);
        ctx.lineTo(arrowX, y + height * 0.2);
        ctx.stroke();

        // Arrow head
        ctx.beginPath();
        ctx.moveTo(arrowX, y + height * 0.2);
        ctx.lineTo(arrowX - 5, y + height * 0.2 - 10);
        ctx.lineTo(arrowX + 5, y + height * 0.2 - 10);
        ctx.closePath();
        ctx.fill();

        // Energy label
        ctx.font = 'bold 11px Arial';
        ctx.textAlign = 'center';
        ctx.fillStyle = theme.palette.mode === 'dark' ? '#fff' : '#000';
        const energyValue = highlightType === 'dch' ? E_DCH_max : E_CH_max;
        const energyLabel = highlightType === 'dch'
          ? `E_DCH,max=${energyValue.toFixed(1)} [J]`
          : `E_CH,max=${energyValue.toFixed(1)} [J]`;
        ctx.fillText(energyLabel, arrowX, arrowY - 5);
      }

      // Axis labels
      ctx.fillStyle = theme.palette.mode === 'dark' ? '#aaa' : '#666';
      ctx.font = '10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Time t [s]', x + width / 2, y + height + 18);

      ctx.save();
      ctx.translate(x - 25, y + height / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText('Power [W]', 0, 0);
      ctx.restore();

      // Time range labels
      ctx.font = '9px Arial';
      ctx.fillStyle = theme.palette.mode === 'dark' ? '#888' : '#666';
      ctx.fillText('0', x - 5, y + height + 12);
      ctx.fillText(highlightType === 'breaking' ? '130' : '20', x + width + 5, y + height + 12);
    };

    // Draw triangular breaking energy visualization for Module 2
    const drawBreakingTriangle = (x: number, y: number, width: number, height: number) => {
      // Triangle area (pink/red shaded)
      ctx.fillStyle = 'rgba(255, 99, 132, 0.3)';
      ctx.beginPath();
      ctx.moveTo(x, y);                    // Top left (P_0)
      ctx.lineTo(x + width, y + height);   // Bottom right
      ctx.lineTo(x, y + height);           // Bottom left
      ctx.closePath();
      ctx.fill();

      // Triangle border
      ctx.strokeStyle = 'rgba(255, 99, 132, 0.8)';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Axes
      ctx.strokeStyle = theme.palette.mode === 'dark' ? '#fff' : '#000';
      ctx.lineWidth = 2;

      // Y-axis (Power)
      ctx.beginPath();
      ctx.moveTo(x, y + height);
      ctx.lineTo(x, y - 10);
      ctx.stroke();

      // Arrow
      ctx.beginPath();
      ctx.moveTo(x, y - 10);
      ctx.lineTo(x - 4, y - 5);
      ctx.moveTo(x, y - 10);
      ctx.lineTo(x + 4, y - 5);
      ctx.stroke();

      // X-axis (Time)
      ctx.beginPath();
      ctx.moveTo(x, y + height);
      ctx.lineTo(x + width + 10, y + height);
      ctx.stroke();

      // Arrow
      ctx.beginPath();
      ctx.moveTo(x + width + 10, y + height);
      ctx.lineTo(x + width + 5, y + height - 4);
      ctx.moveTo(x + width + 10, y + height);
      ctx.lineTo(x + width + 5, y + height + 4);
      ctx.stroke();

      // Labels
      ctx.fillStyle = theme.palette.mode === 'dark' ? '#fff' : '#000';
      ctx.font = 'bold 11px Arial';
      ctx.textAlign = 'left';
      ctx.fillText(`P_0=${P_0.toFixed(0)}[W]`, x + 10, y + 15);
      ctx.fillText(`E_max=${E_max.toFixed(0)}[J]`, x + width / 3, y + height / 2);

      ctx.font = '10px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('0', x - 5, y + height + 15);
      ctx.fillText(`t_br=${t_br.toFixed(1)}[s]`, x + width / 2, y + height + 20);

      ctx.save();
      ctx.translate(x - 25, y + height / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.fillText('Power', 0, 0);
      ctx.restore();

      ctx.fillText('Time', x + width / 2, y + height + 35);
    };

    // Title
    ctx.fillStyle = theme.palette.mode === 'dark' ? '#fff' : '#000';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Considering Breaking Energy', displayWidth / 2, 25);

    // Module 1: Left side - two power profiles
    const module1X = padding;
    const profile1Y = 70;
    const profile2Y = profile1Y + topHeight + chartSpacing;

    drawPowerProfile(module1X, profile1Y, sectionWidth - 40, topHeight - 40, 'dch');
    drawPowerProfile(module1X, profile2Y, sectionWidth - 40, topHeight - 40, 'ch');

    // Module 1 label and formula
    const module1FormulaY = profile2Y + topHeight + 20;
    ctx.fillStyle = theme.palette.mode === 'dark' ? '#fff' : '#000';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Module 1:', module1X + sectionWidth / 2, module1FormulaY);

    ctx.font = '12px Arial';
    const W_SC_used_1 = E_DCH_max + E_CH_max;
    ctx.fillText(`W_SC,used = E_DCH,max + E_CH,max`, module1X + sectionWidth / 2, module1FormulaY + 20);
    ctx.font = 'bold 13px Arial';
    ctx.fillText(`= ${W_SC_used_1.toFixed(1)} J`, module1X + sectionWidth / 2, module1FormulaY + 38);

    // Module 2: Right side - triangle visualization
    const module2X = module1X + sectionWidth + 40;
    const triangleY = 120;
    const triangleWidth = 180;
    const triangleHeight = 140;

    drawBreakingTriangle(module2X + 40, triangleY, triangleWidth, triangleHeight);

    // Module 2 label and formula
    const module2FormulaY = triangleY + triangleHeight + 60;
    ctx.fillStyle = theme.palette.mode === 'dark' ? '#fff' : '#000';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Module 2:', module2X + sectionWidth / 2, module2FormulaY);

    ctx.font = '12px Arial';
    const W_SC_used_2 = E_DCH_max + E_max;
    ctx.fillText(`W_SC,used = E_DCH,max + E_max`, module2X + sectionWidth / 2, module2FormulaY + 20);
    ctx.font = 'bold 13px Arial';
    ctx.fillText(`= ${W_SC_used_2.toFixed(1)} J`, module2X + sectionWidth / 2, module2FormulaY + 38);

    // Bottom: Capacitance formula
    const formulaY = displayHeight - 80;
    
    ctx.fillStyle = theme.palette.mode === 'dark' ? '#fff' : '#000';
    ctx.font = 'bold 14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText('Capacitance Formula:', displayWidth / 2, formulaY);
    
    ctx.font = 'bold 16px Arial';
    ctx.fillText('C₀ = ', displayWidth / 2 - 150, formulaY + 35);

    ctx.font = '14px Arial';
    ctx.fillText('2 · W_SC,used', displayWidth / 2 - 20, formulaY + 25);

    // Fraction line
    ctx.strokeStyle = theme.palette.mode === 'dark' ? '#fff' : '#000';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(displayWidth / 2 - 70, formulaY + 30);
    ctx.lineTo(displayWidth / 2 + 70, formulaY + 30);
    ctx.stroke();

    ctx.font = '14px Arial';
    ctx.fillText('K · (U_C0,max² - U_C0,min²)', displayWidth / 2 - 20, formulaY + 50);

  }, [E_DCH_max, E_CH_max, P_0, t_br, E_max, theme]);

  return (
    <Box sx={{ width: '100%' }}>
      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: 'auto',
          display: 'block',
          borderRadius: '8px'
        }}
      />
    </Box>
  );
};

export default BreakingEnergyChart;
